import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type Owner from '@ember/owner';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { eq } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import {
  PhUsers,
  PhChartBar,
  PhTrendUp,
  PhTarget,
  PhArrowUp,
  PhWarning,
  PhCheckCircle,
  PhClock,
  PhGraduationCap,
  PhBriefcase,
  PhUsersFour,
  PhChartLine,
  PhSparkle,
  PhLightningA,
  PhQueue,
  PhCurrencyDollar,
} from 'ember-phosphor-icons';
import StatCard from 'tio-reimagined/components/stat-card';
import Card from 'tio-reimagined/components/card';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { LiveTasData } from 'tio-employee/services/reimagined-data';
import {
  getCurrentDemoClientData,
  type DemoClientData,
} from 'tio-employee/utils/career-pathways-demo-data';

export default class CareerPathwaysAdminDashboard extends Component {
  @service declare reimaginedData: ReimaginedDataService;

  @tracked tasData?: LiveTasData;
  @tracked tasUsedAmount?: { yearToDate: number; isLiveData: boolean };

  constructor(owner: Owner, args: object) {
    super(owner, args);
    void this.loadTasMetrics();
  }

  loadTasMetrics = async () => {
    const [tasData, tasUsedAmount] = await Promise.all([
      this.reimaginedData.fetchTasData(),
      this.reimaginedData.fetchTasUsedAmount(),
    ]);
    this.tasData = tasData;
    this.tasUsedAmount = tasUsedAmount;
  };

  get tasSource(): DataSource {
    if (this.reimaginedData.isDemoMode) return 'demo';
    if (!this.tasData) return 'placeholder';
    return this.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get demoData(): DemoClientData {
    return getCurrentDemoClientData();
  }

  get client() {
    return this.demoData.client;
  }

  get criticalRoles() {
    return this.demoData.criticalRoles;
  }

  get topPrograms() {
    return this.demoData.topPrograms;
  }

  get pipeline() {
    return this.demoData.pipeline;
  }

  // AI Insights based on client context
  get aiInsights() {
    const clientId = this.client.id;
    if (clientId === 'ups') {
      return [
        {
          type: 'risk',
          title: '5 drivers at risk of turnover',
          description: 'They have completed training but lack clear advancement path',
          action: 'Review career plans',
        },
        {
          type: 'opportunity',
          title: 'CDL training ROI exceeds target by 23%',
          description: 'Consider expanding program capacity',
          action: 'View analysis',
        },
        {
          type: 'action',
          title: '12 supervisors ready for promotion',
          description: 'Pipeline is strong, budget permits 8 promotions',
          action: 'Start reviews',
        },
      ];
    } else if (clientId === 'starbucks') {
      return [
        {
          type: 'risk',
          title: '8 baristas paused ASU enrollment',
          description: 'Scheduling conflicts detected in recent survey',
          action: 'Review schedules',
        },
        {
          type: 'opportunity',
          title: 'District manager pipeline healthy',
          description: '15 store managers are program-ready',
          action: 'View pipeline',
        },
        {
          type: 'action',
          title: '23 employees graduating this quarter',
          description: 'Recognize achievements and discuss career goals',
          action: 'Plan events',
        },
      ];
    }
    // Advocate (healthcare) default
    return [
      {
        type: 'risk',
        title: '3 RNs at flight risk',
        description: 'High performers without active development plans',
        action: 'Engage employees',
      },
      {
        type: 'opportunity',
        title: 'BSN program showing 340% ROI',
        description: 'Nurse retention 2.3x higher for participants',
        action: 'View report',
      },
      {
        type: 'action',
        title: '7 nurse managers ready for director track',
        description: 'Leadership assessment scores above threshold',
        action: 'Review candidates',
      },
    ];
  }

  // Pending approvals
  get pendingApprovals() {
    const clientId = this.client.id;
    if (clientId === 'ups') {
      return [
        { name: 'Marcus J.', program: 'CDL-A Training', amount: 4500, submitted: '2h ago' },
        { name: 'Jennifer S.', program: 'Supply Chain Cert', amount: 2800, submitted: '5h ago' },
        { name: 'Robert K.', program: 'Supervisor Development', amount: 1500, submitted: '1d ago' },
      ];
    } else if (clientId === 'starbucks') {
      return [
        { name: 'Ashley M.', program: 'ASU Online - Business', amount: 3200, submitted: '1h ago' },
        { name: 'Tyler R.', program: 'Barista Champion Cert', amount: 450, submitted: '3h ago' },
        { name: 'Maria G.', program: 'Store Manager Program', amount: 1800, submitted: '6h ago' },
      ];
    }
    return [
      { name: 'Sarah M.', program: 'RN-to-BSN', amount: 5500, submitted: '1h ago' },
      { name: 'James K.', program: 'Leadership Essentials', amount: 2200, submitted: '4h ago' },
      { name: 'Maria R.', program: 'Clinical Specialty', amount: 3800, submitted: '1d ago' },
    ];
  }

  // Budget utilization
  get budgetData() {
    const clientId = this.client.id;
    if (clientId === 'ups') {
      return { used: 2.1, total: 3.5, percentage: 60 };
    } else if (clientId === 'starbucks') {
      return { used: 18.5, total: 25, percentage: 74 };
    }
    return { used: 4.2, total: 5.5, percentage: 76 };
  }

  // Generate recent activity based on client context
  get recentActivity() {
    const clientId = this.client.id;
    if (clientId === 'ups') {
      return [
        {
          type: 'completion',
          employee: 'Mike T.',
          action: 'completed CDL-A Training',
          time: '2 hours ago',
        },
        {
          type: 'enrollment',
          employee: 'Lisa M.',
          action: 'enrolled in Supervisor Leadership',
          time: '4 hours ago',
        },
        {
          type: 'promotion',
          employee: 'Carlos R.',
          action: 'promoted to Hub Supervisor',
          time: '1 day ago',
        },
        {
          type: 'milestone',
          employee: 'James K.',
          action: 'reached 75% of career plan',
          time: '2 days ago',
        },
      ];
    } else if (clientId === 'starbucks') {
      return [
        {
          type: 'completion',
          employee: 'Emma S.',
          action: 'completed ASU semester',
          time: '1 hour ago',
        },
        {
          type: 'enrollment',
          employee: 'Jordan M.',
          action: 'enrolled in Store Manager Development',
          time: '3 hours ago',
        },
        {
          type: 'promotion',
          employee: 'Taylor R.',
          action: 'promoted to Shift Supervisor',
          time: '1 day ago',
        },
        {
          type: 'milestone',
          employee: 'Alex P.',
          action: 'earned 30 credits toward degree',
          time: '2 days ago',
        },
      ];
    }
    // Advocate (healthcare) default
    return [
      {
        type: 'completion',
        employee: 'Sarah M.',
        action: 'completed Leadership Essentials',
        time: '2 hours ago',
      },
      {
        type: 'enrollment',
        employee: 'James K.',
        action: 'enrolled in RN-to-BSN program',
        time: '4 hours ago',
      },
      {
        type: 'promotion',
        employee: 'Maria R.',
        action: 'promoted to Nurse Manager',
        time: '1 day ago',
      },
      {
        type: 'milestone',
        employee: 'David L.',
        action: 'reached 50% of career plan',
        time: '2 days ago',
      },
    ];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0 pb-12">
      {{! Header }}
      <div class="flex justify-between items-center mb-6">
        <div>
          <Heading @level={{1}} class="text-2xl font-bold text-gray-900">
            Career Pathways Dashboard
          </Heading>
          <Text class="text-gray-600">{{this.client.name}}
            —
            {{this.client.employeeCount}}
            employees</Text>
        </div>
        <div class="flex gap-3">
          <Button @appearance="outlined" @intent="primary">
            <PhChartBar @size={{18}} class="mr-2" />
            Export Report
          </Button>
          <Button @intent="primary">
            <PhTarget @size={{18}} class="mr-2" />
            Configure Goals
          </Button>
        </div>
      </div>

      {{! AI Insights Banner }}
      <div
        class="mb-6 p-4 bg-gradient-to-r from-violet-500/10 to-teal-500/10 dark:from-violet-900/30 dark:to-teal-900/30 rounded-xl border border-violet-200 dark:border-violet-800"
      >
        <div class="flex items-center gap-3 mb-4">
          <div
            class="p-2 rounded-lg bg-gradient-to-br from-violet-500 to-teal-500 text-white shadow-lg"
          >
            <PhSparkle @size={{20}} weight="fill" />
          </div>
          <div>
            <Text class="font-semibold text-gray-900">AI Insights</Text>
            <Text class="text-sm text-gray-600">3 recommendations for your attention</Text>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          {{#each this.aiInsights as |insight|}}
            <div class="p-3 bg-white/70 dark:bg-gray-800/70 rounded-lg border border-gray-100">
              <div class="flex items-start gap-2 mb-2">
                {{#if (eq insight.type "risk")}}
                  <PhWarning @size={{16}} class="text-red-500 mt-0.5" />
                {{else if (eq insight.type "opportunity")}}
                  <PhTrendUp @size={{16}} class="text-green-500 mt-0.5" />
                {{else}}
                  <PhLightningA @size={{16}} class="text-amber-500 mt-0.5" />
                {{/if}}
                <Text class="font-medium text-gray-900 text-sm">{{insight.title}}</Text>
              </div>
              <Text class="text-sm text-gray-600 mb-2">{{insight.description}}</Text>
              <button
                type="button"
                class="text-sm font-medium text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300"
              >
                {{insight.action}}
                →
              </button>
            </div>
          {{/each}}
        </div>
      </div>

      {{! KPI Cards }}
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          @icon={{PhUsers}}
          @label="TA Utilization"
          @value="{{this.client.taUtilization}}%"
          @color="violet"
        >
          <:extra>
            <div class="flex items-center gap-2">
              <span class="text-sm text-green-600 dark:text-green-400 flex items-center">
                <PhArrowUp @size={{14}} />
                3.2%
              </span>
              <Text class="text-gray-600 text-sm">
                {{#if (eq this.client.id "starbucks")}}
                  Highest in retail sector
                {{else if (eq this.client.id "ups")}}
                  Opportunity for growth
                {{else}}
                  Strong for healthcare
                {{/if}}
              </Text>
            </div>
          </:extra>
        </StatCard>

        <StatCard
          @icon={{PhTarget}}
          @label="Plan Activation Rate"
          @value="{{this.client.planActivation}}%"
          @color="teal"
        >
          <:extra>
            <div class="flex items-center gap-2">
              <span class="text-sm text-green-600 dark:text-green-400 flex items-center">
                <PhArrowUp @size={{14}} />
                8.5%
              </span>
              <Text class="text-gray-600 text-sm">Employees with active career plans</Text>
            </div>
          </:extra>
        </StatCard>

        <StatCard
          @icon={{PhGraduationCap}}
          @label="Program Completion"
          @value="{{this.client.completionRate}}%"
          @color="green"
        >
          <:extra>
            <div class="flex items-center gap-2">
              <span class="text-sm text-green-600 dark:text-green-400 flex items-center">
                <PhArrowUp @size={{14}} />
                5.1%
              </span>
              <Text class="text-gray-600 text-sm">Of enrolled complete programs</Text>
            </div>
          </:extra>
        </StatCard>

        <StatCard
          @icon={{PhTrendUp}}
          @label="Internal Mobility"
          @value="{{this.client.internalMobility}}%"
          @color="amber"
        >
          <:extra>
            <div class="flex items-center gap-2">
              <span class="text-sm text-green-600 dark:text-green-400 flex items-center">
                <PhArrowUp @size={{14}} />
                2.8%
              </span>
              <Text class="text-gray-600 text-sm">Promotions from TA participants (12mo)</Text>
            </div>
          </:extra>
        </StatCard>
      </div>

      {{! TAS Metrics }}
      {{#if this.tasData}}
        <DataIndicator
          @source={{this.tasSource}}
          @showBadge={{true}}
          @dataSourceId="admin-tas-metrics"
        >
          <Card class="mb-6">
            <div class="flex items-center justify-between mb-4">
              <div>
                <Heading @level={{2}} class="text-lg font-semibold text-gray-900">
                  Tuition Assistance Metrics
                </Heading>
                <Text class="text-sm text-gray-600">
                  {{t "tuition_assistance.reimagined.dashboard.section_momentum"}}
                </Text>
              </div>
              <Chip @intent={{if (eq this.tasSource "live") "success" "warning"}} @size="sm">
                {{if (eq this.tasSource "live") "Live" "Demo"}}
              </Chip>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div class="rounded-xl border border-gray-200 p-4">
                <div class="flex items-center gap-2 text-sm text-gray-600">
                  <PhTarget @size={{16}} class="text-violet-600" />
                  {{t "tuition_assistance.reimagined.dashboard.metric_approved"}}
                </div>
                <Heading @level={{3}} class="text-gray-900">
                  {{formatCurrency this.tasData.summary.totalApproved}}
                </Heading>
              </div>
              <div class="rounded-xl border border-gray-200 p-4">
                <div class="flex items-center gap-2 text-sm text-gray-600">
                  <PhCurrencyDollar @size={{16}} class="text-emerald-600" />
                  {{t "tuition_assistance.reimagined.dashboard.metric_paid"}}
                </div>
                <Heading @level={{3}} class="text-gray-900">
                  {{formatCurrency this.tasData.summary.totalPaid}}
                </Heading>
              </div>
              <div class="rounded-xl border border-gray-200 p-4">
                <div class="flex items-center gap-2 text-sm text-gray-600">
                  <PhCheckCircle @size={{16}} class="text-indigo-600" />
                  {{t "tuition_assistance.reimagined.dashboard.metric_active"}}
                </div>
                <Heading @level={{3}} class="text-gray-900">
                  {{this.tasData.summary.activeCount}}
                </Heading>
              </div>
              <div class="rounded-xl border border-gray-200 p-4">
                <div class="flex items-center gap-2 text-sm text-gray-600">
                  <PhClock @size={{16}} class="text-amber-600" />
                  {{t "tuition_assistance.reimagined.dashboard.metric_pending"}}
                </div>
                <Heading @level={{3}} class="text-gray-900">
                  {{this.tasData.summary.pendingCount}}
                </Heading>
              </div>
            </div>
            {{#if this.tasUsedAmount}}
              <div class="mt-4 text-sm text-gray-600">
                {{t "tuition_assistance.reimagined.dashboard.card_used_this_year"}}
                ·
                {{formatCurrency this.tasUsedAmount.yearToDate}}
              </div>
            {{/if}}
          </Card>
        </DataIndicator>
      {{/if}}

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {{! Main Column }}
        <div class="lg:col-span-2 space-y-6">
          {{! Budget + Approvals Row }}
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            {{! Budget Utilization }}
            <Card>
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center gap-2">
                  <PhCurrencyDollar @size={{20}} class="text-green-600 dark:text-green-400" />
                  <Text class="font-semibold text-gray-900">Budget Utilization</Text>
                </div>
                <Text class="text-sm text-gray-600">FY 2024</Text>
              </div>
              <div class="flex items-center justify-center mb-4">
                {{! SVG Progress Ring }}
                <div class="relative">
                  <svg width="120" height="120" viewBox="0 0 120 120" class="transform -rotate-90">
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="12"
                      class="text-gray-200 dark:text-gray-700"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="12"
                      stroke-linecap="round"
                      stroke-dasharray="314"
                      stroke-dashoffset="{{if
                        (eq this.client.id 'ups')
                        '126'
                        (if (eq this.client.id 'starbucks') '82' '75')
                      }}"
                      class="text-green-500 dark:text-green-400"
                    />
                  </svg>
                  <div
                    class="absolute inset-0 flex flex-col items-center justify-center text-center"
                  >
                    <span
                      class="text-2xl font-bold text-gray-900"
                    >{{this.budgetData.percentage}}%</span>
                    <span class="text-xs text-gray-500">utilized</span>
                  </div>
                </div>
              </div>
              <div class="text-center">
                <Text class="text-gray-900 font-semibold">${{this.budgetData.used}}M</Text>
                <Text class="text-gray-500">
                  of ${{this.budgetData.total}}M budget</Text>
              </div>
            </Card>

            {{! Pending Approvals }}
            <Card>
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center gap-2">
                  <PhQueue @size={{20}} class="text-violet-600 dark:text-violet-400" />
                  <Text class="font-semibold text-gray-900">Pending Approvals</Text>
                </div>
                <Chip @intent="warning" @size="sm">{{this.pendingApprovals.length}}</Chip>
              </div>
              <div class="space-y-3">
                {{#each this.pendingApprovals as |approval|}}
                  <div
                    class="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
                  >
                    <div class="min-w-0">
                      <Text
                        class="font-medium text-gray-900 text-sm truncate"
                      >{{approval.name}}</Text>
                      <Text class="text-xs text-gray-500 truncate">{{approval.program}}</Text>
                    </div>
                    <div class="text-right shrink-0">
                      <Text class="font-semibold text-gray-900 text-sm">${{approval.amount}}</Text>
                      <Text class="text-xs text-gray-500">{{approval.submitted}}</Text>
                    </div>
                  </div>
                {{/each}}
              </div>
              <button
                type="button"
                class="mt-3 w-full py-2 text-sm font-medium text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 rounded-lg transition-colors"
              >
                View All Approvals →
              </button>
            </Card>
          </div>

          {{! Critical Role Shortages }}
          <Card @padding="none">
            <div class="flex items-center justify-between p-5 border-b border-gray-100">
              <div class="flex items-center gap-2">
                <PhWarning @size={{20}} class="text-red-500" />
                <Text class="font-semibold text-gray-900">Critical Role Shortages</Text>
              </div>
              <LinkTo @route="authenticated.reimagined.admin.skills-inventory">
                <Button @appearance="outlined" @size="sm">View All Gaps</Button>
              </LinkTo>
            </div>
            <div class="p-5 space-y-4">
              {{#each this.criticalRoles as |role|}}
                <div class="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <Text class="font-semibold text-gray-900">{{role.title}}</Text>
                      {{#if (eq role.risk "critical")}}
                        <Chip @intent="danger" @size="sm">Critical</Chip>
                      {{else if (eq role.risk "high")}}
                        <Chip @intent="warning" @size="sm">High</Chip>
                      {{else}}
                        <Chip @intent="default" @size="sm">Medium</Chip>
                      {{/if}}
                    </div>
                    <div class="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span class="text-red-600 dark:text-red-400 font-medium">Gap:
                        {{role.gap}}</span>
                      <span>Retirements: {{role.retirements}}</span>
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <PhUsers @size={{16}} />
                      <span class="font-semibold">{{role.pipeline}}</span>
                    </div>
                    <Text class="text-gray-500 text-sm">in pipeline</Text>
                  </div>
                </div>
              {{/each}}
            </div>
          </Card>

          {{! Top Performing Programs }}
          <Card @padding="none">
            <div class="flex items-center justify-between p-5 border-b border-gray-100">
              <div class="flex items-center gap-2">
                <PhChartLine @size={{20}} class="text-violet-600 dark:text-violet-400" />
                <Text class="font-semibold text-gray-900">Top Performing Programs</Text>
              </div>
              <LinkTo @route="authenticated.reimagined.admin.program-effectiveness">
                <Button @appearance="outlined" @size="sm">View All Programs</Button>
              </LinkTo>
            </div>
            <div class="p-5 overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="text-left text-sm text-gray-500 border-b">
                    <th class="pb-3 font-medium">Program</th>
                    <th class="pb-3 font-medium">Provider</th>
                    <th class="pb-3 font-medium text-center">Enrolled</th>
                    <th class="pb-3 font-medium text-center">Completion</th>
                    <th class="pb-3 font-medium text-right">ROI</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  {{#each this.topPrograms as |program|}}
                    <tr>
                      <td class="py-3">
                        <Text class="font-medium text-gray-900">{{program.name}}</Text>
                      </td>
                      <td class="py-3 text-gray-600 text-sm">{{program.provider}}</td>
                      <td class="py-3 text-center text-gray-600">{{program.enrollments}}</td>
                      <td class="py-3 text-center">
                        <Chip @intent="success" @size="sm">{{program.completionRate}}%</Chip>
                      </td>
                      <td
                        class="py-3 text-right font-semibold text-green-600 dark:text-green-400"
                      >{{program.roi}}%</td>
                    </tr>
                  {{/each}}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {{! Sidebar }}
        <div class="space-y-6">
          {{! Leadership Pipeline }}
          <Card @padding="none">
            <div class="flex items-center justify-between p-5 border-b border-gray-100">
              <div class="flex items-center gap-2">
                <PhUsersFour @size={{20}} class="text-violet-600 dark:text-violet-400" />
                <Text class="font-semibold text-gray-900">Leadership Pipeline</Text>
              </div>
              <LinkTo @route="authenticated.reimagined.admin.leadership">
                <Button @appearance="outlined" @size="sm">Manage</Button>
              </LinkTo>
            </div>
            <div class="p-5 space-y-3">
              <div
                class="flex items-center justify-between p-3 bg-violet-50 dark:bg-violet-900/30 rounded-lg"
              >
                <div>
                  <Text class="font-semibold text-gray-900">Identified</Text>
                  <Text class="text-gray-500 text-sm">High potential</Text>
                </div>
                <span
                  class="text-2xl font-bold text-violet-600 dark:text-violet-400"
                >{{this.pipeline.identified}}</span>
              </div>
              <div
                class="flex items-center justify-between p-3 bg-violet-100 dark:bg-violet-900/50 rounded-lg"
              >
                <div>
                  <Text class="font-semibold text-gray-900">In Program</Text>
                  <Text class="text-gray-500 text-sm">Actively developing</Text>
                </div>
                <span
                  class="text-2xl font-bold text-violet-700 dark:text-violet-300"
                >{{this.pipeline.inProgram}}</span>
              </div>
              <div
                class="flex items-center justify-between p-3 bg-teal-50 dark:bg-teal-900/30 rounded-lg"
              >
                <div>
                  <Text class="font-semibold text-gray-900">Developing</Text>
                  <Text class="text-gray-500 text-sm">In training</Text>
                </div>
                <span
                  class="text-2xl font-bold text-teal-600 dark:text-teal-400"
                >{{this.pipeline.developing}}</span>
              </div>
              <div
                class="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/30 rounded-lg"
              >
                <div>
                  <Text class="font-semibold text-gray-900">Ready Now</Text>
                  <Text class="text-gray-500 text-sm">Promotion ready</Text>
                </div>
                <span
                  class="text-2xl font-bold text-green-600 dark:text-green-400"
                >{{this.pipeline.readyNow}}</span>
              </div>
              <div class="pt-3 mt-3 border-t border-gray-200">
                <div class="flex items-center justify-between text-sm">
                  <Text class="text-gray-500">Promoted YTD</Text>
                  <Text
                    class="font-semibold text-green-600 dark:text-green-400"
                  >{{this.pipeline.promoted}}</Text>
                </div>
                <div class="flex items-center justify-between text-sm mt-2">
                  <Text class="text-gray-500">Attrition</Text>
                  <Text
                    class="font-semibold text-red-600 dark:text-red-400"
                  >{{this.pipeline.attrition}}</Text>
                </div>
              </div>
            </div>
          </Card>

          {{! Recent Activity }}
          <Card @padding="none">
            <div class="flex items-center gap-2 p-5 border-b border-gray-100">
              <PhClock @size={{20}} class="text-gray-500" />
              <Text class="font-semibold text-gray-900">Recent Activity</Text>
            </div>
            <div class="p-5 space-y-4">
              {{#each this.recentActivity as |activity|}}
                <div class="flex items-start gap-3">
                  <div class="mt-1">
                    {{#if (eq activity.type "completion")}}
                      <PhCheckCircle @size={{18}} class="text-green-500" />
                    {{else if (eq activity.type "enrollment")}}
                      <PhGraduationCap @size={{18}} class="text-violet-500" />
                    {{else if (eq activity.type "promotion")}}
                      <PhTrendUp @size={{18}} class="text-amber-500" />
                    {{else}}
                      <PhTarget @size={{18}} class="text-teal-500" />
                    {{/if}}
                  </div>
                  <div class="flex-1 min-w-0">
                    <Text class="text-gray-900">
                      <span class="font-medium">{{activity.employee}}</span>
                      {{activity.action}}
                    </Text>
                    <Text class="text-gray-500 text-sm">{{activity.time}}</Text>
                  </div>
                </div>
              {{/each}}
            </div>
          </Card>

          {{! Quick Links }}
          <Card @padding="none">
            <div class="flex items-center gap-2 p-5 border-b border-gray-100">
              <PhBriefcase @size={{20}} class="text-gray-500" />
              <Text class="font-semibold text-gray-900">Quick Actions</Text>
            </div>
            <div class="p-5 space-y-2">
              <LinkTo
                @route="authenticated.reimagined.admin.team"
                class="block p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
              >
                <Text class="text-violet-600 dark:text-violet-400 font-medium">View Team Progress</Text>
              </LinkTo>
              <LinkTo
                @route="authenticated.reimagined.admin.skills-inventory"
                class="block p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
              >
                <Text class="text-violet-600 dark:text-violet-400 font-medium">Skills Inventory</Text>
              </LinkTo>
              <LinkTo
                @route="authenticated.reimagined.admin.leadership"
                class="block p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
              >
                <Text class="text-violet-600 dark:text-violet-400 font-medium">Leadership Pipeline</Text>
              </LinkTo>
              <LinkTo
                @route="authenticated.reimagined.admin.program-effectiveness"
                class="block p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
              >
                <Text class="text-violet-600 dark:text-violet-400 font-medium">Program ROI</Text>
              </LinkTo>
            </div>
          </Card>
        </div>
      </div>
    </VStack>
  </template>
}
